import apiClient from './client';

export interface DashboardStats {
  activeJobs: number;
  inProgressJobs: number;
  scheduledJobs: number;
  completedJobs: number;
  totalJobs: number;
  jobBudget: number;
  jobActualCost: number;

  arOutstanding: number;
  collectedTotal: number;
  invoicedTotal: number;
  overdueCount: number;
  overdueAmount: number;
  awaitingPayment: number;

  pendingQuotes: number;
  approvedQuotes: number;
  pipelineValue: number;

  pendingEstimates: number;
  pendingEstimateValue: number;

  totalClients: number;
  activeEmployees: number;
  totalCrews: number;
  catalogProducts: number;
  upcomingScheduleCount: number;
}

export interface UpcomingJob {
  id: number;
  job_number: string;
  title: string;
  client: string;
  date: string | null;
  endDate: string | null;
  status: string;
  budget: number | null;
  crew: string | null;
}

export interface Alert {
  id: string;
  icon: string;
  iconColor: 'warning' | 'critical' | 'success' | 'primary';
  title: string;
  description: string;
  link: string;
}

export interface CalendarEvent {
  date: string;
  type: 'schedule' | 'job-start' | 'job-end';
  label: string;
  jobId: number;
}

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await apiClient.get('/dashboard/stats');
  return response.data.data;
};

export const getUpcomingJobs = async (): Promise<UpcomingJob[]> => {
  const response = await apiClient.get('/dashboard/upcoming-jobs');
  return response.data.data;
};

export const getAlerts = async (): Promise<Alert[]> => {
  const response = await apiClient.get('/dashboard/alerts');
  return response.data.data;
};

export const getCalendarEvents = async (): Promise<CalendarEvent[]> => {
  const response = await apiClient.get('/dashboard/calendar-events');
  return response.data.data;
};

export interface RevenueTrendPoint {
  month: string;
  invoiced: number;
  collected: number;
}

export interface ActivityItem {
  type: 'job' | 'invoice' | 'quote';
  id: number;
  ref: string;
  label: string;
  status: string;
  client: string;
  ts: string;
}

export const getRevenueTrend = async (): Promise<RevenueTrendPoint[]> => {
  const response = await apiClient.get('/dashboard/revenue-trend');
  return response.data.data;
};

export const getRecentActivity = async (): Promise<ActivityItem[]> => {
  const response = await apiClient.get('/dashboard/recent-activity');
  return response.data.data;
};

// ── Needs attention (data-quality gaps, CR #77) ─────────────────────────────

export const getNeedsAttention = async (): Promise<Alert[]> => {
  const response = await apiClient.get('/dashboard/needs-attention');
  return response.data.data;
};

// ── Daily log quick entry (CR #88) ──────────────────────────────────────────

export interface DailyLogJobStatus {
  id: number;
  title: string;
  job_number: string;
  client_id: number | null;
  start_date: string | null;      // "YYYY-MM-DD"
  logged_today: boolean;
  log_dates: string[];            // "YYYY-MM-DD" per log in the last 14 days (CR #91)
}

export const getDailyLogStatus = async (): Promise<DailyLogJobStatus[]> => {
  const response = await apiClient.get('/dashboard/daily-log-status');
  return response.data.data;
};

// ── Job task lists (CR #87) — planner cards for active jobs ─────────────────

export interface JobTaskCard {
  id: number;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  start_date: string | null;
  completed_at: string | null;
  job_id: number;
  job_title: string;
  job_number: string;
  client_id: number | null;
  assignee_name: string | null;
  checklist_total: number;
  checklist_done: number;
}

export const getJobTaskLists = async (): Promise<JobTaskCard[]> => {
  const response = await apiClient.get('/dashboard/job-tasks');
  return response.data.data;
};
